import React, { useEffect, useRef } from "react";

const MONO = { fontFamily: "'JetBrains Mono','Fira Code',monospace" };

/**
 * MenuBar — CAD-style menu bar: File | Edit | View | Draw | Tools
 * Renders dropdowns on click, closes on outside click or Escape.
 */
export default function MenuBar({
  activeMenu, setActiveMenu,
  onNew, onSave, onPrint, onExportSVG, onExportDXF,
  onExportWallSchedule, onExportOpeningSchedule, onExportRoomSchedule, onExportBOQ,
  onUndo, onRedo, onClear, onSelectAll,
  onToggleGrid, onToggleLayers, onToggleProperties, onToggleCommandLine, onToggleMenuBar,
  onFullscreen, onZoomFit, onZoomIn, onZoomOut,
  onTemplates, onDrawing, onHeal,
  onProjectEdit, onToggle3D, show3D,
  onToggleAI, showAI,
  onExportGCodeRouter, onExportGCodeLaser, onExportCutList, onExportDXFFull,
  onDrawWall, onDrawLine, onDrawRoom, onDrawOpening, onDrawAnnotation, onDrawDimension, onDrawGridLine,
  showLayerPanel, showCmdLine, isFullscreen,
}) {
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setActiveMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [setActiveMenu]);

  const menus = [
    {
      id: "file", label: "File",
      items: [
        { label: "New Project",         shortcut: "",      action: onNew },
        { label: "Project Settings…",   shortcut: "",      action: onProjectEdit },
        { type: "sep" },
        { label: "Save",                shortcut: "Ctrl+S",action: onSave },
        { label: "Export SVG",          shortcut: "",      action: onExportSVG },
        { label: "Export DXF",          shortcut: "",      action: onExportDXF },
        { label: "Export DXF (full — circles/arcs)", shortcut: "", action: onExportDXFFull },
        { type: "sep" },
        { label: "Wall Schedule (CSV)", shortcut: "",      action: onExportWallSchedule },
        { label: "Opening Schedule (CSV)", shortcut: "",   action: onExportOpeningSchedule },
        { label: "Room Schedule (CSV)", shortcut: "",      action: onExportRoomSchedule },
        { label: "Full BOQ (CSV)",      shortcut: "",      action: onExportBOQ },
        { label: "Cut List CSV (CNC)",  shortcut: "",      action: onExportCutList },
        { type: "sep" },
        { label: "G-code — CNC Router", shortcut: "",     action: onExportGCodeRouter },
        { label: "G-code — Laser Cutter", shortcut: "",   action: onExportGCodeLaser },
        { type: "sep" },
        { label: "Print / PDF",         shortcut: "Ctrl+P",action: onPrint },
      ],
    },
    {
      id: "edit", label: "Edit",
      items: [
        { label: "Undo",    shortcut: "Ctrl+Z", action: onUndo },
        { label: "Redo",    shortcut: "Ctrl+Y", action: onRedo },
        { type: "sep" },
        { label: "Select All",  shortcut: "Ctrl+A", action: onSelectAll },
        { label: "Clear Canvas",shortcut: "C",      action: onClear },
        { type: "sep" },
        { label: "Auto-Heal Topology", shortcut: "H", action: onHeal },
      ],
    },
    {
      id: "view", label: "View",
      items: [
        { label: isFullscreen ? "Exit Full Screen" : "Full Screen",
          shortcut: "F11", action: onFullscreen },
        { label: show3D ? "✓ 3D Preview" : "  3D Preview",
          shortcut: "3", action: onToggle3D },
        { label: showAI ? "✓ AI Assistant" : "  AI Assistant",
          shortcut: "Ctrl+J", action: onToggleAI },
        { type: "sep" },
        { label: "Zoom In",  shortcut: "Scroll+", action: onZoomIn },
        { label: "Zoom Out", shortcut: "Scroll-", action: onZoomOut },
        { label: "Fit to Canvas", shortcut: "",   action: onZoomFit },
        { type: "sep" },
        { label: showLayerPanel ? "✓ Layer Panel"     : "  Layer Panel",
          shortcut: "L", action: onToggleLayers },
        { label: "  Properties Panel", shortcut: "",  action: onToggleProperties },
        { label: showCmdLine ? "✓ Command Line"  : "  Command Line",
          shortcut: "`", action: onToggleCommandLine },
        { label: "  Menu Bar",   shortcut: "M",   action: onToggleMenuBar },
      ],
    },
    {
      id: "draw", label: "Draw",
      items: [
        { label: "Wall",        shortcut: "W", action: onDrawWall },
        { label: "Line",        shortcut: "W", action: onDrawLine },
        { label: "Room Label",  shortcut: "R", action: onDrawRoom },
        { label: "Opening",     shortcut: "O", action: onDrawOpening },
        { label: "Annotation",  shortcut: "A", action: onDrawAnnotation },
        { label: "Dimension",   shortcut: "X", action: onDrawDimension },
        { label: "Grid Line",   shortcut: "G", action: onDrawGridLine },
        { type: "sep" },
        { label: "Templates…",  shortcut: "T", action: onTemplates },
      ],
    },
    {
      id: "tools", label: "Tools",
      items: [
        { label: "Construction Drawings…", shortcut: "D", action: onDrawing },
        { type: "sep" },
        { label: "Heal Topology",    shortcut: "H", action: onHeal },
      ],
    },
  ];

  return (
    <div ref={ref} style={{
      height: 26, background: "#030508", borderBottom: "1px solid #0d1520",
      display: "flex", alignItems: "stretch", padding: "0 4px",
      flexShrink: 0, userSelect: "none",
      ...MONO,
    }}>
      {/* App logo */}
      <div style={{ padding: "0 10px", display:"flex", alignItems:"center",
        fontSize:9, color:"#2a3a4a", letterSpacing:2, fontWeight:800 }}>
        J.A.D.E
      </div>

      {/* Menu items */}
      {menus.map(menu => (
        <div key={menu.id} style={{ position: "relative" }}>
          <button
            onClick={() => setActiveMenu(activeMenu === menu.id ? null : menu.id)}
            style={{
              height: "100%", padding: "0 10px", background: activeMenu === menu.id ? "#06111e" : "transparent",
              border: "none", borderBottom: activeMenu === menu.id ? "1px solid #3b82f6" : "1px solid transparent",
              color: activeMenu === menu.id ? "#dde6f0" : "#4a5e70",
              fontSize: 10, cursor: "pointer", ...MONO,
            }}>
            {menu.label}
          </button>

          {activeMenu === menu.id && (
            <div style={{
              position: "absolute", top: "100%", left: 0, zIndex: 200,
              background: "#04080f", border: "1px solid #0d1520",
              borderTop: "none", minWidth: 210,
              boxShadow: "0 8px 24px #000c",
            }}>
              {menu.items.map((item, i) => {
                if (item.type === "sep") return (
                  <div key={i} style={{ height: 1, background: "#0d1520", margin: "2px 0" }}/>
                );
                return (
                  <button key={i}
                    onClick={() => { setActiveMenu(null); item.action?.(); }}
                    style={{
                      width: "100%", padding: "5px 14px", textAlign: "left",
                      background: "transparent", border: "none",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      color: "#6b7e94", fontSize: 10, cursor: "pointer", ...MONO,
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = "#06111e"; e.currentTarget.style.color = "#dde6f0"; }}
                    onMouseOut={e  => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#6b7e94"; }}>
                    <span>{item.label}</span>
                    {item.shortcut && (
                      <span style={{ fontSize: 9, color: "#2a3a4a", marginLeft: 20 }}>{item.shortcut}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
